import { readAccessTokenClaims, type AccessTokenClaims } from "./jwtClaims";

/**
 * Silent renewal of the signed-in user's access token.
 *
 * Tenant tokens are minted by api-service's `POST /api/v2/auth/select-tenant`
 * with a short lifetime (ACCESS_TOKEN_TTL_MINUTES, 60 in the deployed values),
 * and there is no refresh token. Without renewal the session simply dies when
 * that token expires. This module re-mints the token for the same tenant
 * shortly before it expires, using the still-valid token as the credential.
 * Every mint re-reads the account and membership on the server, so a
 * deactivated user or a revoked membership still stops at the next renewal.
 *
 * An expired token cannot be renewed: the server rejects it like any other
 * request. So renewal has to happen ahead of expiry, which is what the timer
 * and the pre-request check are for.
 */

/** Renew this long before expiry (shortened for very short-lived tokens). */
export const RENEW_LEAD_MS = 5 * 60 * 1000;
/** Up to this much extra lead, at random, so several open tabs don't all renew at once. */
export const RENEW_JITTER_MS = 30 * 1000;
/** A request about to go out with a token this close to expiry renews first. */
export const PRE_REQUEST_RENEW_MS = 60 * 1000;
/** After a transient failure (network, 5xx), the timer tries again this much later. */
export const RENEW_RETRY_MS = 30 * 1000;

export interface AccessTokenRenewerDeps {
  readToken: () => string | null;
  writeToken: (token: string) => void;
  /** Mints a replacement token for `tenantId`, authenticated by `currentToken`. */
  mintToken: (tenantId: string, currentToken: string) => Promise<string>;
  now?: () => number;
  random?: () => number;
  setTimer?: (callback: () => void, delayMs: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface AccessTokenRenewer {
  /**
   * Renew the stored token now. Single-flight: concurrent callers share one
   * request. Rejects when the stored token cannot be renewed, or the server
   * refuses; a token the server refused is never sent to it again.
   */
  renew: () => Promise<string>;
  /** Note the token about to be sent, and (re)arm the timer when it changed. */
  track: (token: string | null) => void;
  /**
   * The token to send with a request: renewed first when it is about to
   * expire, otherwise `token` as given. Never rejects — a failed renewal
   * sends the old token and the 401 path decides.
   */
  tokenForRequest: (token: string) => Promise<string>;
  stop: () => void;
}

/** Thrown when the stored session has nothing renewable. */
export class SessionNotRenewableError extends Error {
  constructor(reason: string) {
    super(`Session cannot be renewed: ${reason}`);
    this.name = "SessionNotRenewableError";
  }
}

/**
 * Only an ordinary tenant token can be re-minted through select-tenant.
 * A platform token has no tenant to select. An impersonation token (`imp`)
 * is excluded on purpose: select-tenant mints a plain token for the account,
 * so renewing through it would drop the record that a superadmin is acting.
 */
export const renewableTenantId = (claims: AccessTokenClaims | null): string | null => {
  if (!claims || claims.typ !== "tenant" || claims.impersonatedBy) return null;
  return claims.tenantId;
};

/** When to renew, as epoch ms, or `null` when the token has no usable expiry. */
export const renewAtMs = (claims: AccessTokenClaims, jitter: number): number | null => {
  if (claims.expiresAtMs === null) return null;
  const lifetime =
    claims.issuedAtMs !== null ? claims.expiresAtMs - claims.issuedAtMs : RENEW_LEAD_MS * 2;
  const lead = Math.min(RENEW_LEAD_MS + jitter * RENEW_JITTER_MS, Math.max(0, lifetime / 2));
  return claims.expiresAtMs - lead;
};

/** A definitive refusal (401/403) as opposed to a network blip or a 5xx. */
const isRefusal = (error: unknown): boolean => {
  const status = (error as { status?: number } | null)?.status;
  return status === 401 || status === 403;
};

export const createAccessTokenRenewer = ({
  readToken,
  writeToken,
  mintToken,
  now = () => Date.now(),
  random = Math.random,
  setTimer = (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimer = (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}: AccessTokenRenewerDeps): AccessTokenRenewer => {
  let inFlight: Promise<string> | null = null;
  let refusedToken: string | null = null;
  let trackedToken: string | null = null;
  let timer: unknown = null;

  const clearScheduled = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };

  const runRenewal = async (): Promise<string> => {
    const current = readToken();
    if (!current) throw new SessionNotRenewableError("no stored token");
    if (current === refusedToken) throw new SessionNotRenewableError("token was refused");
    const tenantId = renewableTenantId(readAccessTokenClaims(current));
    if (!tenantId) throw new SessionNotRenewableError("not a renewable tenant token");

    let minted: string;
    try {
      minted = await mintToken(tenantId, current);
    } catch (error) {
      if (isRefusal(error)) refusedToken = current;
      throw error;
    }

    const stored = readToken();
    // Signed out while the mint was in flight: never bring the session back.
    if (!stored) throw new SessionNotRenewableError("signed out during renewal");
    // Replaced meanwhile (tenant switch, another tab): that token wins.
    if (stored !== current) {
      track(stored);
      return stored;
    }
    writeToken(minted);
    track(minted);
    return minted;
  };

  const renew = (): Promise<string> => {
    if (!inFlight) {
      inFlight = runRenewal().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };

  const schedule = (atMs: number) => {
    clearScheduled();
    timer = setTimer(onTimer, Math.max(0, atMs - now()));
  };

  const onTimer = () => {
    timer = null;
    const current = readToken();
    if (!current) {
      trackedToken = null;
      return;
    }
    // Someone else (another tab, a tenant switch) already replaced it.
    if (current !== trackedToken) {
      track(current);
      return;
    }
    renew().catch(() => {
      const expiresAtMs = readAccessTokenClaims(current)?.expiresAtMs ?? 0;
      const retryAt = now() + RENEW_RETRY_MS;
      if (readToken() === current && current !== refusedToken && retryAt < expiresAtMs) {
        schedule(retryAt);
      }
    });
  };

  function track(token: string | null) {
    if (token === trackedToken) return;
    trackedToken = token;
    clearScheduled();
    if (!token) return;
    const claims = readAccessTokenClaims(token);
    if (!renewableTenantId(claims) || !claims) return;
    const at = renewAtMs(claims, random());
    if (at !== null) schedule(at);
  }

  const tokenForRequest = async (token: string): Promise<string> => {
    track(token);
    const claims = readAccessTokenClaims(token);
    if (!renewableTenantId(claims) || claims?.expiresAtMs == null) return token;
    const remaining = claims.expiresAtMs - now();
    // Already expired: renewal would be refused too; let the 401 path decide once.
    if (remaining <= 0 || remaining > PRE_REQUEST_RENEW_MS) return token;
    try {
      return await renew();
    } catch {
      return token;
    }
  };

  const stop = () => {
    clearScheduled();
    trackedToken = null;
  };

  return { renew, track, tokenForRequest, stop };
};
